import {FormControl} from '@mui/material'
import {Box, Button, Typography} from '@viptech/react-components'
import {useState} from 'react'
import {Input} from 'src/components/input/Input'
import ModalLateral, {ModalLateralFooter} from 'src/components/modal/ModalLateral'
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectItem,
  MultiSelectLabel,
  MultiSelectScrollView,
  MultiSelectValue,
  SelectValue,
} from 'src/components/multi-select/MultiSelect'
import useFaceLists from 'src/hooks/api/facial/useFaceLists'
import useUpdateFace from 'src/hooks/api/facial/useUpdateFace'
import centeredPhotoExample from 'src/images/centeredPhotoExample.svg'
import goodDistanceExample from 'src/images/goodDistanceExample.svg'
import NoAccessoriesExample from 'src/images/NoAccessoriesExample.svg'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import {FaceDetection} from 'src/services/api'
import createStyleSheet from 'src/utilities/createStyleSheet'
import PhotoCard from './PhotoCard'

interface Props {
  faceDetectionSelected: FaceDetection | null
  closeModal: () => void
  onReturn: () => void
}

export default function AddFaceModal({faceDetectionSelected, closeModal, onReturn}: Props) {
  const {selectedCompanies} = useCompaniesSelector()

  const {mutateAsync} = useUpdateFace()

  const [selectedLists, setSelectedLists] = useState<Array<SelectValue>>([])
  const {data, isLoading} = useFaceLists({companyIds: selectedCompanies.map((it) => it.id)})
  const [name, setName] = useState('')

  if (!faceDetectionSelected) return null

  const onSubmit = async () => {
    await mutateAsync({
      id: faceDetectionSelected.face?.id!,
      recognitionId: faceDetectionSelected.id,
      name: name,
      listsIds: selectedLists.map((item) => parseInt(item.value)),
    })
    closeModal()
  }

  return (
    <ModalLateral isOpen={!!faceDetectionSelected} closeModal={closeModal} title='Cadastrar face' onReturn={onReturn}>
      <Box display='flex' direction='column' rowGap='24px' style={styles.overflow} maxHeight='80vh' flex={1}>
        <Box display='flex' direction='column' rowGap='20px' marginTop='4px'>
          <FormControl fullWidth>
            <Input.Root
              labelFontSize='12px'
              borderColor='#D0D5DD'
              fontSize='14px'
              fontWeight='400'
              labelFontWeight='400'
              placeholder='Digite o nome completo'
              label='Nome completo'
              maxLength={99}
              min={4}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <MultiSelect onChange={setSelectedLists} value={selectedLists} loading={isLoading}>
              <MultiSelectLabel labelFontSize='12px' labelFontWeight='400' label={'Listas'} />
              <MultiSelectValue placeholder={'Selecione uma ou mais listas'} width='100%' />
              <MultiSelectContent>
                <MultiSelectScrollView>
                  {data?.data.data.entities.map((item, index) => (
                    <MultiSelectItem key={index} value={item.id.toString()} label={item.name}>
                      {item.name}
                    </MultiSelectItem>
                  ))}
                </MultiSelectScrollView>
              </MultiSelectContent>
            </MultiSelect>
          </FormControl>
        </Box>

        <Box display='flex' direction='column' rowGap='48px'>
          <Typography variant='h3' size='16px' weight={600}>
            Verifique a imagem capturada e confirme se ela atende aos requisitos
          </Typography>
          <Box display='flex' flex={1} justifyContent='center' align='center' rowGap='48px' direction='column'>
            <PhotoCard
              src={`${process.env.REACT_APP_CROPPED_IMAGES_BASE_URL}/${faceDetectionSelected.cropImageKey}`}
              width='170px'
              height='200px'
              style={{outline: '1px dashed #D7D7D7', outlineOffset: '12px', outlineWidth: '2px'}}
            />

            <Box display='flex' flex={1} justifyContent='space-evenly' padding='0 60px' width='100%'>
              <Box width='150px' display='flex' justifyContent='center' rowGap='8px' direction='column' align='center'>
                <Box width='132px' height='132px' border='1px solid #8E8E8E' display='flex' align='flex-end' justifyContent='center'>
                  <img src={centeredPhotoExample} alt='Centered example' />
                </Box>
                <Typography variant='label' size='12px' textAlign='center'>
                  Certifique-se de que a face está bem alinhada, e centralizada na imagem
                </Typography>
              </Box>
              <Box width='150px' display='flex' justifyContent='center' rowGap='8px' direction='column' align='center'>
                <Box width='132px' height='132px' border='1px solid #8E8E8E' display='flex' align='flex-end' justifyContent='center'>
                  <img src={goodDistanceExample} alt='Good distance' />
                </Box>
                <Typography variant='label' size='12px' textAlign='center'>
                  Verifique se a face esta em uma boa distância e com uma boa resolução
                </Typography>
              </Box>
              <Box width='180px' display='flex' justifyContent='center' rowGap='8px' direction='column' align='center'>
                <Box width='132px' height='132px' border='1px solid #8E8E8E' display='flex' align='center' justifyContent='center'>
                  <img src={NoAccessoriesExample} alt='No accessories example' />
                </Box>
                <Typography variant='label' size='12px' textAlign='center'>
                  Evite imagens com acessórios no rosto que dificulte a visualização da face.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <ModalLateralFooter>
        <Box display='flex' flex={1} justifyContent='flex-end' padding='0 40px' columnGap='12px'>
          <Button width='150px' height='48px' variant='outlined' onClick={() => onReturn()}>
            Cancelar
          </Button>
          <Button
            width='150px'
            height='48px'
            variant='contained'
            onClick={onSubmit}
            isLoading={isLoading}
            disabledTextColor='#B9B9B9'
            disabled={!name || !selectedLists.length || isLoading}>
            Salvar
          </Button>
        </Box>
      </ModalLateralFooter>
    </ModalLateral>
  )
}

const styles = createStyleSheet({
  overflow: {
    paddingRight: 6,
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    '-ms-overflow-style': 'none',
  },
})
